// The Transaction Wizard runner — the ONE client every host mounts (the
// platform Run view, the downloaded runtime, the J2EE client, the designer's
// Preview) with its own WizardHost (docs/plans/transaction-wizard.md §10).
//
// Handheld (a narrow screen, or `presentation.density: "handheld"`): one step
// per screen, big targets, the context strip above the inputs, tools in a
// "More" sheet, Enter = Next. Desktop: the path taken on the left, the step in
// the middle, context panels and the record so far on the right, tools as a
// toolbar. The application theme throughout — no per-wizard styling.

import { useEffect, useMemo, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ChevronRight, Info, Loader2, MoreHorizontal,
  PauseCircle, RotateCcw, Wand2, X,
} from 'lucide-react'
import { useIsMobile } from '../txn/compact/useIsMobile'
import { PageIcon } from '../iconMap'
import { tw } from '../testHooks'
import type { WizardRunnerProps, WizardTool } from './contract'
import { isInteractive, liveRows, nextRoute, readCol, stepById, text } from './model'
import { useWizard, type WizardController } from './useWizard'
import ContextPanels from './context/ContextPanels'
import StepBody, { StartStep } from './steps'
import { display } from './steps/common'
import { BAD, GOOD, INFO, T, WARN, banner, button, card, muted } from './styles'

export default function WizardRunner(props: WizardRunnerProps) {
  const ctl = useWizard(props)
  const isMobile = useIsMobile()
  const density = ctl.model?.presentation?.density
  const handheld = density === 'handheld' ? true : density === 'desktop' ? false : isMobile
  const [sheet, setSheet] = useState(false)
  const { model, step, scope, frame } = ctl

  useEffect(() => { setSheet(false) }, [frame?.cursor])

  const tools = useMemo(() => {
    const all = [...(model?.tools || []), ...(step?.tools || [])]
    return all.filter(t => ctl.toolVisible(t))
  }, [model, step, ctl])

  const title = model?.title ? (scope ? text(model.title, scope) : model.title) : (props.wizard)
  const stepTitle = step && scope ? text(step.title, scope) : ''
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return
    const el = e.target as HTMLElement
    if (['TEXTAREA', 'BUTTON', 'SELECT', 'A'].includes(el.tagName) || el.isContentEditable) return
    if (el.closest('[data-tw-dialog],[role="dialog"],[role="alertdialog"]')) return
    if (ctl.phase !== 'ready' || !step || !isInteractive(step.kind) || step.kind === 'grid') return
    e.preventDefault()
    void ctl.next()
  }

  return (
    <div {...tw('wizard', props.wizard, { kind: 'U' })} onKeyDown={onKeyDown}
      style={{
        display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
        background: 'var(--color-bg, #fff)', color: T.text, fontFamily: 'inherit',
      }}>
      <Header ctl={ctl} title={title} stepTitle={stepTitle} handheld={handheld}
        onMore={tools.length || handheld ? () => setSheet(true) : undefined} />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Body ctl={ctl} handheld={handheld} tools={tools} />
      </div>
      {ctl.phase === 'ready' && step && <Footer ctl={ctl} handheld={handheld} />}
      {sheet && <MoreSheet ctl={ctl} tools={tools} handheld={handheld} onClose={() => setSheet(false)} />}
      {ctl.modals}
    </div>
  )
}

// ── Header: title, breadcrumb, progress ──────────────────────────────────

function Header({ ctl, title, stepTitle, handheld, onMore }: {
  ctl: WizardController; title: string; stepTitle: string; handheld: boolean; onMore?: () => void
}) {
  const { breadcrumb, repeat, path, remaining, phase } = ctl
  // The CURRENT step's place on the path — after Back it is not the count of
  // every step visited so far.
  const at = ctl.frame ? path.indexOf(ctl.frame.cursor) : -1
  const stepNo = at >= 0 ? at + 1 : Math.max(path.length, 1)
  return (
    <div style={{
      flexShrink: 0, borderBottom: `1px solid ${T.border}`, background: T.band,
      padding: handheld ? '10px 12px' : '12px 18px', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      {breadcrumb.length > 1 && (
        <div style={{ ...muted, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontSize: 13 }}>
          {breadcrumb.map((b, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {i > 0 && <ChevronRight size={12} />}
              <span style={{ fontWeight: i === breadcrumb.length - 1 ? 600 : 400 }}>{b.title}</span>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Wand2 size={handheld ? 18 : 16} style={{ color: T.accent, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: handheld ? 17 : 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {stepTitle || title}
          </div>
          {stepTitle && <div style={{ ...muted, fontSize: 13 }}>{title}</div>}
        </div>
        {onMore && phase === 'ready' && (
          <button type="button" onClick={onMore} style={{ ...button(handheld), padding: '0 12px' }}
            aria-label="More" {...tw('wizard-tool', '_more')}>
            <MoreHorizontal size={18} />
          </button>
        )}
      </div>
      {phase === 'ready' && (
        <div style={{ ...muted, fontSize: 13, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {repeat && <span {...tw('msg', 'progress')}>Line {repeat.index} of {repeat.total}</span>}
          <span>Step {stepNo}{remaining > 0 ? ` · about ${remaining} more` : ''}</span>
        </div>
      )}
      {breadcrumb.length > 1 && phase === 'ready' && (
        <div style={{ ...banner(INFO), padding: '6px 10px', fontSize: 13 }}>
          <PauseCircle size={14} />
          {ctl.assignee
            ? ` Requested from "${breadcrumb[breadcrumb.length - 2].title}" — it is handed back when you finish or cancel.`
            : ` ${breadcrumb[breadcrumb.length - 2].title} is paused until you finish or cancel this.`}
        </div>
      )}
    </div>
  )
}

// ── Body ─────────────────────────────────────────────────────────────────

function Body({ ctl, handheld, tools }: { ctl: WizardController; handheld: boolean; tools: WizardTool[] }) {
  const { phase, step, model, frame } = ctl
  const pad = handheld ? 12 : 18
  if (phase === 'loading' || (phase === 'ready' && !step)) {
    return (
      <div style={{ padding: pad, ...muted, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Loader2 size={16} /> {ctl.busy || 'Opening…'}
      </div>
    )
  }
  if (phase === 'error') {
    return (
      <div style={{ padding: pad, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={banner(BAD)}><AlertTriangle size={16} /> {ctl.error || 'The wizard could not start.'}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={button(handheld)} onClick={() => void ctl.restart()}><RotateCcw size={16} /> Try again</button>
          {ctl.assignee && (
            <button type="button" style={button(handheld)} onClick={() => void ctl.handBackFailed()} {...tw('action', 'hand_back')}>Hand back</button>
          )}
          {ctl.host.close && <button type="button" style={button(handheld)} onClick={() => ctl.host.close?.({ outcome: 'failed' })}>Close</button>}
        </div>
      </div>
    )
  }
  if (phase === 'finished') {
    const fin = ctl.finished
    return (
      <div style={{ padding: pad, display: 'flex', flexDirection: 'column', gap: 12 }} {...tw('wizard-step', '__finished')}>
        <div style={banner(fin?.outcome === 'cancelled' ? INFO : GOOD)}>
          <CheckCircle2 size={16} /> {fin?.message || 'Done.'}
        </div>
        {!ctl.assignee && (
          <div>
            <button type="button" style={button(handheld, true)} onClick={() => void ctl.restart()} {...tw('action', 'restart')}>
              <RotateCcw size={16} /> Start again
            </button>
          </div>
        )}
      </div>
    )
  }
  if (phase === 'paused') {
    const local = ctl.run?.status === 'suspended' && model?.suspend !== 'server'
    return (
      <div style={{ padding: pad, display: 'flex', flexDirection: 'column', gap: 12 }} {...tw('wizard-step', '__paused')}>
        <div style={banner(WARN)}><PauseCircle size={16} /> {ctl.paused}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(local || ctl.run?.status === 'suspended') && (
            <button type="button" style={button(handheld, true)} onClick={() => ctl.resume()} {...tw('action', 'continue')}>Continue</button>
          )}
          {ctl.host.close && (
            <button type="button" style={button(handheld)} onClick={() => ctl.host.close?.({ outcome: 'pending_approval', txnId: frame?.txn_id ?? null })}>Close</button>
          )}
        </div>
      </div>
    )
  }
  if (phase === 'start') {
    return (
      <div style={{ padding: pad, maxWidth: 560 }}>
        <div style={{ ...muted, marginBottom: 12 }}>A few details first.</div>
        <StartStep ctl={ctl} handheld={handheld} />
      </div>
    )
  }
  if (!step || !model || !frame) return null
  const centre = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}
      {...tw('wizard-step', step.id, { kind: step.kind })}>
      {frame.model_changed && (
        <div style={banner(WARN)}><Info size={16} /> This wizard was changed while your run was paused; it restarted at a safe point.</div>
      )}
      {!handheld && tools.length > 0 && <Toolbar ctl={ctl} tools={tools} handheld={false} />}
      <ContextPanels ctl={ctl} panels={step.context} only="strip" />
      {step.help && ctl.scope && step.kind !== 'print' && <div style={muted}>{text(step.help, ctl.scope)}</div>}
      <IssueList ctl={ctl} handheld={handheld} />
      <StepBody ctl={ctl} handheld={handheld} />
      {handheld && <ContextPanels ctl={ctl} panels={step.context} only="panels" />}
    </div>
  )
  if (handheld) return <div style={{ padding: pad }}>{centre}</div>
  const showRecord = model.presentation?.show_record_panel !== false
  const hasSide = showRecord || (step.context || []).some(p => p.layout !== 'strip')
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: hasSide ? '200px minmax(0, 1fr) 320px' : '200px minmax(0, 1fr)',
      gap: 18, padding: pad, alignItems: 'start',
    }}>
      <Rail ctl={ctl} />
      {centre}
      {hasSide && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <ContextPanels ctl={ctl} panels={step.context} only="panels" />
          {showRecord && <RecordPanel ctl={ctl} />}
        </div>
      )}
    </div>
  )
}

/** The path taken (desktop rail). */
function Rail({ ctl }: { ctl: WizardController }) {
  const { model, frame, path } = ctl
  if (!model || !frame) return null
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {path.map((id) => {
        const s = stepById(model, id)
        const on = id === frame.cursor
        return (
          <li key={id} style={{
            padding: '6px 10px', borderRadius: 6, fontSize: 14,
            background: on ? 'var(--color-accent-soft, #e8f0fe)' : 'transparent',
            color: on ? T.text : T.muted, fontWeight: on ? 600 : 400,
            borderLeft: `3px solid ${on ? T.accent : 'transparent'}`,
          }}>
            {(s?.title && ctl.scope ? text(s.title, ctl.scope) : '') || id}
          </li>
        )
      })}
      {ctl.remaining > 0 && <li style={{ ...muted, padding: '6px 10px', fontSize: 13 }}>about {ctl.remaining} more…</li>}
    </ol>
  )
}

/** What the record holds so far — header values and the lines. */
function RecordPanel({ ctl }: { ctl: WizardController }) {
  const { fi, frame } = ctl
  if (!fi || !frame) return null
  const head = fi.forms[fi.headerNo]
  const fields = (head?.cols || [])
    .filter(c => !c.hidden)
    .map(c => ({ label: String(c.heading || c.name), value: readCol(frame.record.header, fi, fi.headerNo, String(c.name)) }))
    .filter(f => display(f.value) !== '')
    .slice(0, 14)
  const forms = (fi.children[fi.headerNo] || []).map(fno => ({ fno, rows: liveRows(frame.record.details[fno]) }))
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }} data-tw-block="wizard:record">
      <div style={{ fontWeight: 600, fontSize: 14 }}>Record so far{frame.txn_id ? '' : ' (not saved)'}</div>
      {fields.length === 0 && <div style={muted}>Nothing entered yet.</div>}
      {fields.map((f, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, fontSize: 14 }}>
          <span style={{ color: T.muted, minWidth: 0, flex: '0 0 45%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</span>
          <span style={{ flex: 1, overflowWrap: 'anywhere' }}>{display(f.value)}</span>
        </div>
      ))}
      {forms.filter(f => f.rows.length).map(f => {
        const sm = fi.forms[f.fno]
        const cols = (sm?.cols || []).filter(c => !c.hidden)
          .filter(c => f.rows.some(r => display(readCol(r, fi, f.fno, String(c.name))) !== '')).slice(0, 3)
        return (
          <div key={f.fno} style={{ borderTop: `1px solid ${T.border}`, paddingTop: 6 }}>
            <div style={{ ...muted, fontWeight: 600 }}>{sm?.sm.form_title || 'Lines'} · {f.rows.length}</div>
            {f.rows.slice(0, 6).map((r, i) => (
              <div key={i} style={{ fontSize: 13, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {cols.map(c => display(readCol(r, fi, f.fno, String(c.name)))).filter(Boolean).join(' · ')}
              </div>
            ))}
            {f.rows.length > 6 && <div style={{ ...muted, fontSize: 13 }}>and {f.rows.length - 6} more</div>}
          </div>
        )
      })}
    </div>
  )
}

/** Errors and warnings from validate / save, each with "Go to step". */
function IssueList({ ctl, handheld }: { ctl: WizardController; handheld: boolean }) {
  const { issues, model, step, scope } = ctl
  if (!issues.length || !model) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} {...tw('msg', issues.some(i => i.type === 'E') ? 'error' : 'warning')}>
      {issues.map((iss, i) => {
        const target = iss.steps.find(s => s !== step?.id)
        const ts = target ? stepById(model, target) : null
        const tone = iss.type === 'E' ? BAD : WARN
        return (
          <div key={i} style={{ ...banner(tone), alignItems: 'center', flexWrap: 'wrap' }}>
            <AlertTriangle size={16} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 160 }}>
              {iss.label && !iss.steps.length ? <b>{iss.label}: </b> : null}
              {iss.message}
              {iss.row != null ? <span style={{ opacity: 0.8 }}> (line {iss.row + 1})</span> : null}
            </span>
            {ts && (
              <button type="button" style={{ ...button(handheld), minHeight: handheld ? 40 : 28, fontSize: 13 }}
                onClick={() => void ctl.jumpTo(ts.id, iss.row != null ? { form: String(iss.form_no || ''), index: iss.row } : null)}
                {...tw('action', `goto_${ts.id}`)}>
                Go to step: {(ts.title && scope ? text(ts.title, scope) : '') || ts.id}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Tools ────────────────────────────────────────────────────────────────

function ToolButton({ ctl, tool, handheld, onDone }: {
  ctl: WizardController; tool: WizardTool; handheld: boolean; onDone?: () => void
}) {
  return (
    <button type="button" disabled={!!ctl.busy}
      style={{ ...button(handheld, false, !!ctl.busy), justifyContent: handheld ? 'flex-start' : 'center', width: handheld ? '100%' : undefined }}
      onClick={() => { onDone?.(); void ctl.runTool(tool) }} {...tw('wizard-tool', tool.label)}>
      {tool.icon ? <PageIcon name={tool.icon} size={16} /> : null}
      {tool.label}
    </button>
  )
}

function Toolbar({ ctl, tools, handheld }: { ctl: WizardController; tools: WizardTool[]; handheld: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {tools.map(t => <ToolButton key={t.label} ctl={ctl} tool={t} handheld={handheld} />)}
    </div>
  )
}

/** Handheld: tools, the record so far, pause and cancel in a bottom sheet. */
function MoreSheet({ ctl, tools, handheld, onClose }: {
  ctl: WizardController; tools: WizardTool[]; handheld: boolean; onClose: () => void
}) {
  const [record, setRecord] = useState(false)
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,28,40,.45)', zIndex: 1100, display: 'flex', alignItems: 'flex-end' }}
      onClick={onClose} data-tw-dialog="wizard_more">
      <div onClick={e => e.stopPropagation()} style={{
        background: T.bg, width: '100%', maxHeight: '80vh', overflow: 'auto', borderRadius: '14px 14px 0 0',
        padding: 14, display: 'flex', flexDirection: 'column', gap: 8, boxShadow: '0 -10px 40px rgba(0,0,0,.2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ flex: 1, fontWeight: 600 }}>More</span>
          <button type="button" aria-label="Close" style={{ ...button(handheld), padding: '0 12px' }} onClick={onClose}><X size={18} /></button>
        </div>
        {tools.map(t => <ToolButton key={t.label} ctl={ctl} tool={t} handheld onDone={onClose} />)}
        <button type="button" style={{ ...button(true), justifyContent: 'flex-start' }} onClick={() => setRecord(r => !r)}>
          {record ? 'Hide' : 'Show'} the record so far
        </button>
        {record && <RecordPanel ctl={ctl} />}
        <button type="button" style={{ ...button(true), justifyContent: 'flex-start' }} onClick={() => { onClose(); void ctl.pause() }}
          {...tw('action', 'pause')}>
          <PauseCircle size={16} /> Pause
        </button>
        <button type="button" style={{ ...button(true), justifyContent: 'flex-start', color: BAD.fg }} onClick={() => { onClose(); void ctl.cancel() }}
          {...tw('action', 'cancel')}>
          <X size={16} /> {ctl.assignee ? 'Hand back' : ctl.breadcrumb.length > 1 ? 'Cancel and go back' : 'Cancel'}
        </button>
      </div>
    </div>
  )
}

// ── Footer ───────────────────────────────────────────────────────────────

function nextLabel(ctl: WizardController): string | null {
  const { step, scope, ui } = ctl
  if (!step || !scope) return 'Next'
  if (!isInteractive(step.kind)) return null
  if (step.kind === 'scan' && step.loop) {
    const u = String(step.loop.until || '')
    return u.startsWith('button:') ? u.slice(7).trim() || 'Done' : 'Done'
  }
  if (step.kind === 'confirm') return 'Confirm'
  if (step.kind === 'print' && !step.required && !step.file?.repository && !ui.printed) return 'Skip'
  if (step.kind === 'sign' && step.required === false && !ui.signed) return 'Skip'
  if (step.kind === 'send' && step.required === false && !ui.sent) return 'Skip'
  const r = nextRoute(step, scope)
  if (r && 'finish' in r && !Object.keys(ctl.frame?.repeat || {}).length) return 'Finish'
  return 'Next'
}

function Footer({ ctl, handheld }: { ctl: WizardController; handheld: boolean }) {
  const label = nextLabel(ctl)
  const busy = !!ctl.busy
  const content: ReactNode = (
    <>
      <button type="button" style={button(handheld, false, !ctl.backState.ok || busy)} disabled={!ctl.backState.ok || busy}
        title={ctl.backState.reason} onClick={() => ctl.back()} {...tw('wizard-back', '1')}>
        <ArrowLeft size={16} />{handheld ? '' : ' Back'}
      </button>
      {!handheld && (
        <button type="button" style={button(false, false, busy)} disabled={busy} onClick={() => void ctl.cancel()}
          {...tw('action', 'cancel')}>
          {ctl.assignee ? 'Hand back' : ctl.breadcrumb.length > 1 ? 'Cancel and go back' : 'Cancel'}
        </button>
      )}
      {!handheld && (ctl.assignee || ctl.model?.suspend === 'server') && (
        <button type="button" style={button(false, false, busy)} disabled={busy} onClick={() => void ctl.pause()} {...tw('action', 'pause')}>
          <PauseCircle size={16} /> Pause
        </button>
      )}
      <span style={{ flex: 1, ...muted, textAlign: 'center' }}>{busy ? ctl.busy : ''}</span>
      {label && (
        <button type="button" style={{ ...button(handheld, true, busy), flex: handheld ? 1 : undefined, minWidth: 120 }} disabled={busy}
          onClick={() => void ctl.next()} {...tw('wizard-next', '1')}>
          {busy ? <Loader2 size={16} /> : null}{label}{!busy && label !== 'Confirm' ? <ArrowRight size={16} /> : null}
        </button>
      )}
    </>
  )
  return (
    <div style={{
      flexShrink: 0, borderTop: `1px solid ${T.border}`, background: T.bg,
      padding: handheld ? '10px 12px calc(10px + env(safe-area-inset-bottom, 0px))' : '12px 18px',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      {content}
    </div>
  )
}
